/**
 * Module dependencies.
 */

import { Transaction } from 'app/types/transaction';
import { useFeature } from 'app/hooks/requests/use-feature-flags';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDetachBadge } from 'app/hooks/requests/bank-accounts/use-transaction-badges';
import { BadgeAssignPopover } from './badge-assign-popover';
import { BadgePill } from './badge-pill';

/**
 * `Props` type.
 */

type Props = {
  // The automatic transaction tags (switch/split/reposition/project) that render
  // first in the row.
  leading?: React.ReactNode;
  // Whether `leading` actually renders anything, so the row can be skipped when
  // there are neither automatic tags nor (feature-on) user badges.
  hasLeading?: boolean;
  item: Transaction;
};

/**
 * Export `TransactionBadges` — a single badge row: the automatic tags first,
 * then (when the feature is on) the user badges and the add control at the end.
 * Returns `null` when there is nothing to show.
 */

export function TransactionBadges({ hasLeading, item, leading }: Props): JSX.Element | null {
  const enabled = useFeature('transactionBadges');

  if (!enabled && !hasLeading) {
    return null;
  }

  return (
    <div className={'flex flex-wrap items-center gap-1.5'}>
      {leading}

      {enabled && <UserBadges item={item} />}
    </div>
  );
}

/**
 * User badges (removable chips) followed by the add/manage "+" control.
 */

function UserBadges({ item }: { item: Transaction }): JSX.Element {
  const [translate] = useTranslation();
  const detach = useDetachBadge();
  const badges = item.badges ?? [];

  return (
    <>
      {badges.map(badge => (
        <BadgePill
          color={badge.color}
          key={badge.id}
          onRemove={() => detach.mutate({ badgeId: badge.id, transactionId: item.id })}
          removeLabel={translate('bankAccounts.movements.badges.remove')}
        >
          {badge.name}
        </BadgePill>
      ))}

      <BadgeAssignPopover item={item} />
    </>
  );
}
